import { Grid, Paper, Typography, IconButton, Tooltip, Button, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { useAppDispatch } from '../../../../store/store';
import { parentUrl } from '../../../../utils/constants';
import WidgetLoading from '../../Shared/WidgetLoading';
import Authorization from '../../../../utils/Authorization';
import b64toBlob from 'b64-to-blob';
import { fetchMembersWithNoPrimaryDiagnosis, exportMembersWithNoPrimaryDiagnosis } from '../../../Widgets/WidgetService';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';
import { isDefaultDate } from '../../../../utils/common';
import { dateFormatter } from '../../../../utils/timeFormat';

const MembersWithNoPrimaryDiagnosis = (props) => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [membersWithNoPrimaryDiagnosis, setMembersWithNoPrimaryDiagnosis] = useState([]);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [show, setShow] = useState(false);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const ref = useRef(null);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    useEffect(() => {
        const changeFilter = async () => {
            setIsFetched(false);
            const response = await fetchMembersWithNoPrimaryDiagnosis(selectedTeam, showInActiveTeams);
            setMembersWithNoPrimaryDiagnosis(response);
            setIsFetched(true);
        };
        changeFilter();
    }, [selectedTeam, showInActiveTeams]);

    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Name',
            minWidth: 175,
            flex: 1,
            renderCell: (params) => {
                return <>{params.row.LastName} {params.row.FirstName}</>;
            },
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 105,
            flex: 1,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DOB) ? '-' : dateFormatter(new Date(params.row.DOB))}</>;
            },
        },
        {
            field: 'LastCHPVisit',
            headerName: 'Last CHP Visit',
            minWidth: 115,
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastCHPVisit) ? '-' : dateFormatter(new Date(params.row.LastCHPVisit))}</>;
            },
        },
        {
            field: 'NextCHPVisit',
            headerName: 'Next CHP Visit',
            minWidth: 115,
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.NextCHPVisit) ? '-' : dateFormatter(new Date(params.row.NextCHPVisit))}</>;
            },
        },
        {
            field: 'PrimaryProvider',
            headerName: 'Primary Provider',
            minWidth: 175,
            flex: 1,
        },
    ];

    const exportMembersWithNoPrimaryDiagnosys = async () => {
        setDownLoading(true);
        const response = await exportMembersWithNoPrimaryDiagnosis(selectedTeam, showInActiveTeams);
        let blob = b64toBlob(response.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `MembersWithNoPrimaryDiagnosis - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
                className="smartdashboard-widget"
                elevation={5}
                sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
            >
                <Grid container spacing={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography color={'primary'} variant="h6" textAlign={'center'} ref={ref}>
                            Members With No Primary Diagnosis
                            <Tooltip
                                open={show}
                                title={
                                    <div>
                                        <Typography variant="caption">This widget flags members who are missing a primary diagnosis. Licensed providers can use this widget to review these members and assign their primary diagnosis for proper coding.</Typography>
                                    </div>
                                }
                                PopperProps={{ style: { zIndex: 1000 } }}
                            >
                                <IconButton onClick={handleClick}>
                                    <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={selectedTeam}
                            onChange={(id) => setSelectedTeam(id)}
                            showInactive={showInActiveTeams}
                            onToggleInactive={setShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={6}></Grid>
                    <Grid size={2} display={'flex'} maxHeight="55px">
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportMembersWithNoPrimaryDiagnosys()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || membersWithNoPrimaryDiagnosis?.length === 0}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <Box display="flex" position="relative" height='calc(100vh - 285px)'>
                    {
                        !isFetched ? <WidgetLoading />
                            : membersWithNoPrimaryDiagnosis.length > 0 ?
                                <Grid container spacing={2} marginTop={3} sx={{ height: '100%', maxHeight: '500px', overflowY: 'auto', position: 'relative' }}>
                                    <DataGrid
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                        }}
                                        rows={membersWithNoPrimaryDiagnosis}
                                        columns={columns}
                                        paginationModel={{ pageSize: pageSize, page: page }}
                                        pageSizeOptions={[5]}
                                        onPaginationModelChange={(model) => {
                                            setPageSize(model.pageSize);
                                            setPage(model.page);
                                        }}
                                        getRowId={(row) => row.Id}
                                        getRowHeight={() => 'auto'}
                                        sx={{
                                            [`& .${gridClasses.cell}`]: {
                                                py: 1,
                                            },
                                        }}
                                    />
                                </Grid>
                                : <Box display="flex" width="100%" justifyContent="center" alignItems="center">
                                    <Typography variant="h6">No Matching Records</Typography>
                                </Box>
                    }
                </Box>
            </Paper>
        </Grid>
    );
};

export default MembersWithNoPrimaryDiagnosis;
